/**
 * Der Spielplan: Strassenring, Felder, Gelaende und Bauplaetze.
 *
 * Alles hier ist **gerechnet, nicht gezeichnet**. Aus wenigen Kennzahlen (Brettmass,
 * Ellipsenradien, Strassenbreite, Feldanzahl) entstehen die Feldvierecke, die
 * Gelaendepolygone und die Grundrisse der Bauplaetze. Damit hat jeder Bauplatz einen
 * exakten Ankerpunkt, eine feste Grundflaeche und eine feste Ausrichtung - Voraussetzung
 * dafuer, dass ein Gebaeude buendig sitzt statt nach Augenmass gesetzt zu werden.
 *
 * Zwei Fallen, die hier bereits zugeschnappt sind und deshalb ausdruecklich benannt
 * werden (siehe wiki/FuenfSterne/topics/rendering-und-platzierung.md):
 *
 * 1. Innen- und Aussenkante der Strasse nicht verwechseln. `rechts(tangente)` zeigt
 *    bei dieser Fahrtrichtung zur Brettmitte, nicht nach aussen.
 * 2. Bauplaetze gehoeren an die **Strassennormale**, nicht an die Radialrichtung.
 *    Bei einer Ellipse laufen beide bis zu 20 Grad auseinander; radial gesetzte
 *    Grundrisse kippen dann mit einer Ecke auf die Fahrbahn.
 *
 * Warum ein eigener Plan und kein Nachbau: siehe topics/offene-fragen.md (O1, O2)
 * und topics/recht-und-namensgebung.md.
 */
import type { HotelId } from './hotels'
import { HOTELS } from './hotels'
import type { Grundriss, Polygon, Punkt } from '../geometrie'
import { normiere, punkt, rechts, winkelVon } from '../geometrie'

// ---------------------------------------------------------------------------
// Kennzahlen
// ---------------------------------------------------------------------------

export const BRETT = {
  breite: 720,
  tiefe: 620,
  /** Breite des umlaufenden Rahmens. Innerhalb davon liegt die Spielflaeche. */
  rahmen: 24,
} as const

export const SPIELFLAECHE = {
  minX: -BRETT.breite / 2 + BRETT.rahmen,
  maxX: BRETT.breite / 2 - BRETT.rahmen,
  minZ: -BRETT.tiefe / 2 + BRETT.rahmen,
  maxZ: BRETT.tiefe / 2 - BRETT.rahmen,
} as const

export const STRASSE = {
  /** Halbachsen der Mittellinie. */
  rx: 250,
  rz: 200,
  breite: 34,
  felder: 44,
} as const

const HALBE_STRASSE = STRASSE.breite / 2
/** Abstand zwischen Strassenkante und Bauflucht. */
const GEHWEG = 10
/** Anteil des verfuegbaren Abschnitts, den ein Grundriss ausfuellen darf. */
const FUELLGRAD = 0.78
/** Tiefe eines innen liegenden Hotelgelaendes. Dahinter beginnt der Stadtpark. */
const INNEN_TIEFE = 118

/**
 * Vorzeichen fuer `versetzt`. Bei der hier gewaehlten Fahrtrichtung zeigt
 * `rechts(tangente)` zur Brettmitte. Der Test "Kantenrichtung" haelt das fest.
 */
export const NACH_INNEN = 1
export const NACH_AUSSEN = -1

// ---------------------------------------------------------------------------
// Mittellinie, Bogenlaenge, Kanten
// ---------------------------------------------------------------------------

/** Ellipsenpunkt zum Parameter t. t = 0 liegt links, wachsendes t faehrt nach oben. */
function mittellinie(t: number): Punkt {
  return punkt(STRASSE.rx * Math.cos(Math.PI + t), STRASSE.rz * Math.sin(Math.PI + t))
}

function tangente(t: number): Punkt {
  return normiere(
    punkt(-STRASSE.rx * Math.sin(Math.PI + t), STRASSE.rz * Math.cos(Math.PI + t)),
  )
}

/** Punkt im Abstand `abstand` neben der Mittellinie. Vorzeichen siehe NACH_INNEN. */
function versetzt(t: number, abstand: number): Punkt {
  const m = mittellinie(t)
  const n = rechts(tangente(t))
  return punkt(m.x + n.x * abstand, m.z + n.z * abstand)
}

const FEIN = 3600
const bogenlaengen: number[] = new Array<number>(FEIN + 1)
{
  let s = 0
  bogenlaengen[0] = 0
  let vorher = mittellinie(0)
  for (let i = 1; i <= FEIN; i++) {
    const p = mittellinie((i / FEIN) * Math.PI * 2)
    s += Math.hypot(p.x - vorher.x, p.z - vorher.z)
    bogenlaengen[i] = s
    vorher = p
  }
}
export const STRASSENLAENGE = bogenlaengen[FEIN]!

/** Parameter t, bei dem die Bogenlaenge s erreicht ist. */
function tBeiBogenlaenge(s: number): number {
  const ziel = ((s % STRASSENLAENGE) + STRASSENLAENGE) % STRASSENLAENGE
  let lo = 0
  let hi = FEIN
  while (lo + 1 < hi) {
    const m = (lo + hi) >> 1
    if (bogenlaengen[m]! < ziel) lo = m
    else hi = m
  }
  const a = bogenlaengen[lo]!
  const b = bogenlaengen[hi]!
  const f = b === a ? 0 : (ziel - a) / (b - a)
  return ((lo + f) / FEIN) * Math.PI * 2
}

function bogenlaengeBei(t: number): number {
  const x = (((t % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) / (Math.PI * 2)
  const i = Math.min(FEIN - 1, Math.floor(x * FEIN))
  const f = x * FEIN - i
  return bogenlaengen[i]! + (bogenlaengen[i + 1]! - bogenlaengen[i]!) * f
}



/** Bogenlaenge der versetzten Kurve im Abstand `abstand` zwischen zwei Parametern. */
function versetzteLaenge(tA: number, tB: number, abstand: number): number {
  const n = 64
  let s = 0
  let vorher = versetzt(tA, abstand)
  for (let i = 1; i <= n; i++) {
    const p = versetzt(tA + ((tB - tA) * i) / n, abstand)
    s += Math.hypot(p.x - vorher.x, p.z - vorher.z)
    vorher = p
  }
  return s
}

/** Eine Strassenkante als dichte, nach Polarwinkel aufsteigend sortierte Punktfolge. */
function kante(seite: number): Punkt[] {
  const punkte: Punkt[] = []
  for (let i = 0; i < 1440; i++) {
    punkte.push(versetzt((i / 1440) * Math.PI * 2, seite * HALBE_STRASSE))
  }
  return punkte.sort((a, b) => winkelVon(a) - winkelVon(b))
}

/** Die zur Brettmitte zeigende Strassenkante. */
export const INNENKANTE: readonly Punkt[] = kante(NACH_INNEN)
/** Die vom Brett nach aussen zeigende Strassenkante. */
export const AUSSENKANTE: readonly Punkt[] = kante(NACH_AUSSEN)




// ---------------------------------------------------------------------------
// Felder
// ---------------------------------------------------------------------------

export type FeldArt = 'start' | 'kauf' | 'bau' | 'eingang-gratis' | 'bauphase-gratis'

export interface Feld {
  readonly index: number
  readonly art: FeldArt
  /** Mittelpunkt des Feldes auf der Strassenmitte. */
  readonly mitte: Punkt
  /** Polarwinkel des Mittelpunktes in Grad. Bestimmt die Nachbarschaft. */
  readonly winkel: number
  /** Fahrtrichtung im Feldmittelpunkt. */
  readonly richtung: Punkt
  /** Einheitsvektor senkrecht zur Strasse, zur Brettmitte zeigend. */
  readonly normale: Punkt
  /** Ecken des Feldes: hinten-innen, vorn-innen, vorn-aussen, hinten-aussen. */
  readonly ecken: Polygon
  /** Gelaende an der Innenseite bzw. Aussenseite, falls dort ein Hotel liegt. */
  readonly innen: HotelId | null
  readonly aussen: HotelId | null
  /** Beim Betreten dieses Feldes ueberquerte Linien. */
  readonly linien: readonly ('bank' | 'rathaus')[]
}

/**
 * Die Feldarten des Rings.
 *
 * Zusammensetzung (eigene Gestaltung, siehe offene-fragen.md O1): 44 Felder,
 * davon 1 START, 17 Kauf-Felder, 17 Bau-Felder, 5 "Ein Eingang kostenlos" und
 * 4 "Eine Bauphase kostenlos". Kauf und Bau wechseln sich weitgehend ab, die
 * Sonderfelder liegen gleichmaessig verteilt und nie direkt nebeneinander.
 */
const FELDARTEN: readonly FeldArt[] = [
  'start',
  'kauf', 'bau', 'kauf', 'eingang-gratis', 'bau', 'kauf', 'bau',
  'bauphase-gratis', 'kauf', 'bau', 'kauf', 'eingang-gratis', 'bau',
  'kauf', 'bau', 'bauphase-gratis', 'kauf', 'bau', 'kauf', 'eingang-gratis',
  'bau', 'kauf', 'bau', 'bauphase-gratis', 'kauf', 'bau', 'kauf',
  'eingang-gratis', 'bau', 'kauf', 'bau', 'bauphase-gratis', 'kauf', 'bau',
  'kauf', 'eingang-gratis', 'bau', 'kauf', 'bau', 'kauf', 'bau', 'kauf', 'bau',
]

/**
 * Gelaende werden ueber **Feldbereiche** festgelegt, nicht ueber Winkel. Dadurch faellt
 * jede Gelaendegrenze genau mit einer Feldgrenze zusammen: ein Feld gehoert auf jeder
 * Seite zu genau einem Gelaende, und es gibt keine halben Felder an Grundstuecksgrenzen.
 *
 * Innen liegen die vier grossen Hotels (11 Felder Strassenfront je Hotel), aussen die
 * vier kleinen sowie Bank und Rathaus.
 */
const INNENSEKTOREN: readonly (readonly [HotelId, number, number])[] = [
  ['belvedere', 0, 10],
  ['kapitol', 11, 21],
  ['lagune', 22, 32],
  ['regent', 33, 43],
]

export type AussenBereich = HotelId | 'bank' | 'rathaus'

const AUSSENSEKTOREN: readonly (readonly [AussenBereich, number, number])[] = [
  ['koralle', 0, 7],
  ['bank', 8, 12],
  ['maharani', 13, 21],
  ['savanne', 22, 29],
  ['rathaus', 30, 34],
  ['pagode', 35, 43],
]

/**
 * Das Feld, bei dessen Betreten die jeweilige Linie ueberquert ist. Beide Linien
 * liegen in der Mitte des zugehoerigen Aussensektors, also direkt vor dem Gebaeude.
 */
export const LINIEN_FELD = { bank: 10, rathaus: 32 } as const

function sektorFuer<T>(
  sektoren: readonly (readonly [T, number, number])[],
  feld: number,
): T | null {
  for (const [id, von, bis] of sektoren) if (feld >= von && feld <= bis) return id
  return null
}

/** Parametergrenzen eines Feldbereichs auf der Mittellinie. */
function feldGrenzen(vonFeld: number, bisFeld: number): readonly [number, number] {
  const schritt = STRASSENLAENGE / STRASSE.felder
  const tVon = tBeiBogenlaenge(vonFeld * schritt)
  const tBis =
    bisFeld + 1 >= STRASSE.felder ? Math.PI * 2 : tBeiBogenlaenge((bisFeld + 1) * schritt)
  return [tVon, tBis]
}

function baueFelder(): Feld[] {
  const felder: Feld[] = []
  const schritt = STRASSENLAENGE / STRASSE.felder
  for (let i = 0; i < STRASSE.felder; i++) {
    const tA = tBeiBogenlaenge(i * schritt)
    const tB = i + 1 >= STRASSE.felder ? Math.PI * 2 : tBeiBogenlaenge((i + 1) * schritt)
    const tM = tBeiBogenlaenge((i + 0.5) * schritt)
    const aussenBereich = sektorFuer(AUSSENSEKTOREN, i)
    const linien: ('bank' | 'rathaus')[] = []
    if (i === LINIEN_FELD.bank) linien.push('bank')
    if (i === LINIEN_FELD.rathaus) linien.push('rathaus')

    felder.push({
      index: i,
      art: FELDARTEN[i]!,
      mitte: mittellinie(tM),
      winkel: winkelVon(mittellinie(tM)),
      richtung: tangente(tM),
      normale: rechts(tangente(tM)),
      ecken: [
        versetzt(tA, HALBE_STRASSE * NACH_INNEN),
        versetzt(tB, HALBE_STRASSE * NACH_INNEN),
        versetzt(tB, HALBE_STRASSE * NACH_AUSSEN),
        versetzt(tA, HALBE_STRASSE * NACH_AUSSEN),
      ],
      innen: sektorFuer(INNENSEKTOREN, i),
      aussen:
        aussenBereich === null || aussenBereich === 'bank' || aussenBereich === 'rathaus'
          ? null
          : aussenBereich,
      linien,
    })
  }
  return felder
}

export const FELDER: readonly Feld[] = baueFelder()

/** Die Hotelgrundstuecke, an die ein Feld grenzt - hoechstens zwei. */
export function angrenzendeGelaende(feld: Feld): readonly HotelId[] {
  return [feld.innen, feld.aussen].filter((x): x is HotelId => x !== null)
}

// ---------------------------------------------------------------------------
// Gelaende und Bauplaetze
// ---------------------------------------------------------------------------

export interface Gelaende {
  readonly id: AussenBereich
  readonly innenliegend: boolean
  readonly vonFeld: number
  readonly bisFeld: number
  readonly polygon: Polygon
  /** Ein Grundriss je Gebaeude, in Baureihenfolge. */
  readonly bauplaetze: readonly Grundriss[]
  /** Grundriss des Freizeitparks. */
  readonly freizeitpark: Grundriss
}

/**
 * Grundrissmasse je Gelaende. `parkTiefe` ist bewusst kleiner als `tiefe`: ein
 * Freizeitpark ist eine flache, breite Anlage und kein weiterer Klotz.
 */
interface Mass {
  breite: number
  tiefe: number
  parkBreite: number
  parkTiefe: number
}

const MASSE: Record<AussenBereich, Mass> = {
  belvedere: { breite: 62, tiefe: 44, parkBreite: 76, parkTiefe: 34 },
  kapitol: { breite: 68, tiefe: 50, parkBreite: 82, parkTiefe: 38 },
  lagune: { breite: 60, tiefe: 40, parkBreite: 76, parkTiefe: 32 },
  regent: { breite: 66, tiefe: 48, parkBreite: 80, parkTiefe: 38 },
  pagode: { breite: 62, tiefe: 44, parkBreite: 74, parkTiefe: 34 },
  maharani: { breite: 60, tiefe: 44, parkBreite: 74, parkTiefe: 34 },
  savanne: { breite: 62, tiefe: 42, parkBreite: 74, parkTiefe: 34 },
  koralle: { breite: 92, tiefe: 48, parkBreite: 92, parkTiefe: 38 },
  bank: { breite: 96, tiefe: 44, parkBreite: 60, parkTiefe: 28 },
  rathaus: { breite: 96, tiefe: 44, parkBreite: 60, parkTiefe: 28 },
}

/** Laufparameter 0 bis 4 entlang des Rahmens: oben, rechts, unten, links. */
function rahmenParameter(p: Punkt): number {
  const { minX, maxX, minZ, maxZ } = SPIELFLAECHE
  const e = 1e-6
  if (Math.abs(p.z - minZ) < e) return (p.x - minX) / (maxX - minX)
  if (Math.abs(p.x - maxX) < e) return 1 + (p.z - minZ) / (maxZ - minZ)
  if (Math.abs(p.z - maxZ) < e) return 2 + (maxX - p.x) / (maxX - minX)
  return 3 + (maxZ - p.z) / (maxZ - minZ)
}

const RAHMENECKEN_PARAMETER: readonly (readonly [number, Punkt])[] = [
  [0, punkt(SPIELFLAECHE.minX, SPIELFLAECHE.minZ)],
  [1, punkt(SPIELFLAECHE.maxX, SPIELFLAECHE.minZ)],
  [2, punkt(SPIELFLAECHE.maxX, SPIELFLAECHE.maxZ)],
  [3, punkt(SPIELFLAECHE.minX, SPIELFLAECHE.maxZ)],
]

/** Erster Treffer eines Strahls mit dem Rahmen der Spielflaeche. */
function strahlAufRahmen(start: Punkt, richtung: Punkt): Punkt {
  const kandidaten: number[] = []
  if (richtung.x > 1e-9) kandidaten.push((SPIELFLAECHE.maxX - start.x) / richtung.x)
  if (richtung.x < -1e-9) kandidaten.push((SPIELFLAECHE.minX - start.x) / richtung.x)
  if (richtung.z > 1e-9) kandidaten.push((SPIELFLAECHE.maxZ - start.z) / richtung.z)
  if (richtung.z < -1e-9) kandidaten.push((SPIELFLAECHE.minZ - start.z) / richtung.z)
  const t = Math.min(...kandidaten.filter((x) => x > 0))
  return punkt(start.x + richtung.x * t, start.z + richtung.z * t)
}

const BAND_STUETZEN = 48

/**
 * Das Polygon eines Hotelgelaendes.
 *
 * Innen liegende Gelaende sind ein Band fester Tiefe entlang der Strasse; dahinter
 * bleibt die Brettmitte als Stadtpark frei. Aussen liegende Gelaende reichen von der
 * Strasse bis an den Rahmen. Die seitlichen Grenzen sind in beiden Faellen die
 * **Strassennormalen** an den Feldgrenzen - dieselbe Richtung, in der auch die
 * Grundrisse ausgerichtet werden. Nur dadurch kann ein Grundriss am Rand des Sektors
 * nicht ueber die Gelaendegrenze kippen.
 */
function bandPolygon(tVon: number, tBis: number, innenliegend: boolean): Polygon {
  const seite = innenliegend ? NACH_INNEN : NACH_AUSSEN
  const vorne: Punkt[] = []
  for (let i = 0; i <= BAND_STUETZEN; i++) {
    vorne.push(
      versetzt(tVon + ((tBis - tVon) * i) / BAND_STUETZEN, seite * HALBE_STRASSE),
    )
  }
  if (innenliegend) {
    const hinten: Punkt[] = []
    for (let i = 0; i <= BAND_STUETZEN; i++) {
      hinten.push(
        versetzt(
          tVon + ((tBis - tVon) * i) / BAND_STUETZEN,
          seite * (HALBE_STRASSE + INNEN_TIEFE),
        ),
      )
    }
    return [...vorne, ...hinten.reverse()]
  }

  const normaleBei = (t: number): Punkt => {
    const n = rechts(tangente(t))
    return punkt(n.x * seite, n.z * seite)
  }
  const qVon = strahlAufRahmen(vorne[0]!, normaleBei(tVon))
  const qBis = strahlAufRahmen(vorne[vorne.length - 1]!, normaleBei(tBis))

  // Vom Ende zurueck zum Anfang am Rahmen entlang, gegen die Fahrtrichtung.
  const uVon = rahmenParameter(qVon)
  const uBis = rahmenParameter(qBis)
  const start = uBis < uVon ? uBis + 4 : uBis
  const zwischen: Punkt[] = []
  for (const versatz of [4, 0]) {
    for (const [u, ecke] of [...RAHMENECKEN_PARAMETER].reverse()) {
      const uu = u + versatz
      if (uu < start && uu > uVon) zwischen.push(ecke)
    }
  }
  return [...vorne, qBis, ...zwischen, qVon]
}

/**
 * Legt die Grundrisse eines Gelaendes an der Bauflucht entlang der Strasse ab.
 *
 * Der Feldbereich wird in gleich lange Strassenabschnitte geteilt, ein Grundriss je
 * Abschnitt, mittig, parallel zur Strasse. Die erlaubte Breite ergibt sich aus der
 * Bogenlaenge der versetzten Kurve auf der **engsten** Seite des Grundrisses - bei
 * innen liegenden Gelaenden also hinten, bei aussen liegenden vorn. Wer stattdessen
 * mit der Bogenlaenge der Strassenmitte rechnet, bekommt in Kurven Ueberschneidungen.
 */
function bauflucht(
  bereich: AussenBereich,
  innenliegend: boolean,
  tVon: number,
  tBis: number,
  anzahl: number,
): { bauplaetze: Grundriss[]; freizeitpark: Grundriss } {
  const mass = MASSE[bereich]
  const seite = innenliegend ? NACH_INNEN : NACH_AUSSEN
  const sVon = bogenlaengeBei(tVon)
  const sBis = tBis >= Math.PI * 2 - 1e-9 ? STRASSENLAENGE : bogenlaengeBei(tBis)
  const abschnitt = (sBis - sVon) / anzahl

  const alle: Grundriss[] = []
  for (let k = 0; k < anzahl; k++) {
    const istPark = k === anzahl - 1
    const tiefe = istPark ? mass.parkTiefe : mass.tiefe
    const tA = tBeiBogenlaenge(sVon + k * abschnitt)
    const tB =
      sVon + (k + 1) * abschnitt >= STRASSENLAENGE - 1e-9
        ? Math.PI * 2
        : tBeiBogenlaenge(sVon + (k + 1) * abschnitt)
    const tM = tBeiBogenlaenge(sVon + (k + 0.5) * abschnitt)

    const engsteSeite = seite * (HALBE_STRASSE + GEHWEG + (innenliegend ? tiefe : 0))
    const platz = versetzteLaenge(tA, tB, engsteSeite)
    const breite = Math.min(platz * FUELLGRAD, istPark ? mass.parkBreite : mass.breite)

    const n = rechts(tangente(tM))
    const nach = punkt(n.x * seite, n.z * seite)
    const m = mittellinie(tM)
    const abstand = HALBE_STRASSE + GEHWEG + tiefe / 2
    const mitte = punkt(m.x + nach.x * abstand, m.z + nach.z * abstand)

    alle.push({ mitte, breite, tiefe, drehung: Math.atan2(-nach.x, nach.z) })
  }
  return { bauplaetze: alle.slice(0, -1), freizeitpark: alle[alle.length - 1]! }
}

function baueGelaende(): Record<AussenBereich, Gelaende> {
  const out = {} as Record<AussenBereich, Gelaende>
  for (const [id, vonFeld, bisFeld] of INNENSEKTOREN) {
    const [tVon, tBis] = feldGrenzen(vonFeld, bisFeld)
    out[id] = {
      id,
      innenliegend: true,
      vonFeld,
      bisFeld,
      polygon: bandPolygon(tVon, tBis, true),
      ...bauflucht(id, true, tVon, tBis, HOTELS[id].gebaeude.length + 1),
    }
  }
  for (const [id, vonFeld, bisFeld] of AUSSENSEKTOREN) {
    const [tVon, tBis] = feldGrenzen(vonFeld, bisFeld)
    const anzahl = id === 'bank' || id === 'rathaus' ? 2 : HOTELS[id].gebaeude.length + 1
    out[id] = {
      id,
      innenliegend: false,
      vonFeld,
      bisFeld,
      polygon: bandPolygon(tVon, tBis, false),
      ...bauflucht(id, false, tVon, tBis, anzahl),
    }
  }
  return out
}

export const GELAENDE: Readonly<Record<AussenBereich, Gelaende>> = baueGelaende()

/**
 * Die Fahrbahn als Paare von Innen- und Aussenkantenpunkten zum selben Parameter.
 * Zum Zeichnen eines Bandes muessen die Paare zusammenpassen; die nach Polarwinkel
 * sortierten Kantenlisten tun das nicht.
 */
export function strassenBand(
  stuetzen = 720,
): { innen: readonly Punkt[]; aussen: readonly Punkt[] } {
  const innen: Punkt[] = []
  const aussen: Punkt[] = []
  for (let i = 0; i <= stuetzen; i++) {
    const t = ((i % stuetzen) / stuetzen) * Math.PI * 2
    innen.push(versetzt(t, HALBE_STRASSE * NACH_INNEN))
    aussen.push(versetzt(t, HALBE_STRASSE * NACH_AUSSEN))
  }
  return { innen, aussen }
}

/** Eine zur Strassenmitte parallele Kurve, gleichmaessig in t abgetastet. */
export function versetzteKurve(abstand: number, stuetzen = 720): readonly Punkt[] {
  const out: Punkt[] = []
  for (let i = 0; i <= stuetzen; i++) {
    out.push(versetzt(((i % stuetzen) / stuetzen) * Math.PI * 2, abstand))
  }
  return out
}

/** Die Trennstriche zwischen den Feldern, je als Strecke ueber die Fahrbahn. */
export function feldTrenner(): readonly (readonly [Punkt, Punkt])[] {
  const schritt = STRASSENLAENGE / STRASSE.felder
  const out: (readonly [Punkt, Punkt])[] = []
  for (let i = 0; i < STRASSE.felder; i++) {
    const t = tBeiBogenlaenge(i * schritt)
    out.push([
      versetzt(t, HALBE_STRASSE * NACH_INNEN),
      versetzt(t, HALBE_STRASSE * NACH_AUSSEN),
    ])
  }
  return out
}

/** Mitte des Gebaeudes von Bank bzw. Rathaus. */
export function amtsGebaeude(welches: 'bank' | 'rathaus'): Grundriss {
  return GELAENDE[welches].bauplaetze[0]!
}

/** Die Linie, die beim Passieren Bank bzw. Rathaus ausloest, als Strecke. */
export function linienStrecke(welches: 'bank' | 'rathaus'): readonly [Punkt, Punkt] {
  const schritt = STRASSENLAENGE / STRASSE.felder
  const t = tBeiBogenlaenge(LINIEN_FELD[welches] * schritt)
  return [
    versetzt(t, (HALBE_STRASSE + 6) * NACH_INNEN),
    versetzt(t, (HALBE_STRASSE + 6) * NACH_AUSSEN),
  ]
}

/**
 * Wo die Beschriftung eines Gelaendes sitzt und wie sie ausgerichtet ist:
 * mittig im Sektor, mit dem Ruecken zur Strasse, damit sie von der Spielerseite
 * aus lesbar bleibt.
 */
export function gelaendeSchild(id: AussenBereich): Grundriss {
  const gel = GELAENDE[id]
  const [tVon, tBis] = feldGrenzen(gel.vonFeld, gel.bisFeld)
  const tM = (tVon + tBis) / 2
  const seite = gel.innenliegend ? NACH_INNEN : NACH_AUSSEN
  const tiefe = gel.innenliegend ? INNEN_TIEFE * 0.82 : 64
  const n = rechts(tangente(tM))
  const nach = punkt(n.x * seite, n.z * seite)
  const m = mittellinie(tM)
  const abstand = HALBE_STRASSE + tiefe
  return {
    mitte: punkt(m.x + nach.x * abstand, m.z + nach.z * abstand),
    breite: 100,
    tiefe: 26,
    drehung: Math.atan2(-nach.x, nach.z),
  }
}

/** Die freie Flaeche in der Brettmitte: der Stadtpark. */
export function stadtparkRand(stuetzen = 180): readonly Punkt[] {
  const out: Punkt[] = []
  for (let i = 0; i < stuetzen; i++) {
    out.push(versetzt((i / stuetzen) * Math.PI * 2, (HALBE_STRASSE + INNEN_TIEFE) * NACH_INNEN))
  }
  return out
}

/** Die Felder, an denen ein Eingang zu diesem Hotel stehen darf. */
export function eingangsFelder(hotel: HotelId): readonly number[] {
  return FELDER.filter((f) => f.innen === hotel || f.aussen === hotel).map((f) => f.index)
}

export const START_FELD = FELDER.findIndex((f) => f.art === 'start')

/** Position eines Hoteleingangs an einem Feld, zur Darstellung. */
export function eingangsPunkt(feldIndex: number, hotel: HotelId): Punkt {
  const feld = FELDER[feldIndex]!
  const seite = feld.innen === hotel ? NACH_INNEN : NACH_AUSSEN
  return punkt(
    feld.mitte.x + feld.normale.x * seite * HALBE_STRASSE * 0.8,
    feld.mitte.z + feld.normale.z * seite * HALBE_STRASSE * 0.8,
  )
}

/** Mittelpunkt eines Feldes, quer versetzt - damit mehrere Autos nebeneinander passen. */
export function autoPunkt(feldIndex: number, platz: number, plaetze: number): Punkt {
  const feld = FELDER[feldIndex]!
  if (plaetze <= 1) return feld.mitte
  const spanne = STRASSE.breite * 0.45
  const v = (platz / (plaetze - 1) - 0.5) * spanne
  return punkt(feld.mitte.x + feld.normale.x * v, feld.mitte.z + feld.normale.z * v)
}
